import { addCacheBuster, isCacheDisabled } from '@/config/cache-flags';
/**
 * Global Site Configuration Manager
 * 
 * Single source of truth for project type and site configuration.
 * Loads site config once synchronously and caches it globally.
 * No Pinia dependencies - can be used anywhere immediately.
 */

interface SiteConfig {
  siteId: string;
  projectId: string | null;
  projectType: 'white_label' | 'private_label';
  description?: string;
  domains?: string[];
  buildTime?: string;
  buildTimestamp?: number;
  buildHash?: string;
  noGoogleIndex?: boolean;
}

interface GlobalConfig {
  siteConfig: SiteConfig | null;
  projectType: 'white_label' | 'private_label' | null;
  projectId: string | null;
  isLoaded: boolean;
  loadAttempted: boolean;
}

const LOCAL_DEV_FALLBACK: SiteConfig = {
  siteId: 'development-fallback',
  projectId: null,
  projectType: 'white_label',
  description: 'Local development fallback configuration',
  domains: ['localhost', '127.0.0.1']
};

// Global configuration cache - available immediately without any store dependencies
let globalConfig: GlobalConfig = {
  siteConfig: null,
  projectType: null,
  projectId: null,
  isLoaded: false,
  loadAttempted: false
};

/**
 * Load site configuration synchronously
 * This is the ONLY place that should contain site config loading logic
 */
function loadSiteConfigSync(): SiteConfig | null {
  try {
    // PRIORITY 1: Environment variable (for build-time injection)
    const siteConfigJson = import.meta.env.VITE_SITE_CONFIG;
    if (siteConfigJson) {
      const config = JSON.parse(siteConfigJson);
      console.log('⚡ [GlobalSiteConfig] Loaded from build-time env var:', {
        siteId: config.siteId,
        projectId: config.projectId,
        projectType: config.projectType
      });
      return config;
    }
    
    // PRIORITY 2: Runtime file (public/site-config.json)
    const xhr = new XMLHttpRequest();
    // Add cache-busting when cache disabled
    const siteUrl = isCacheDisabled() ? addCacheBuster('/site-config.json') : '/site-config.json';
    xhr.open('GET', siteUrl, false); // Synchronous request
    xhr.send();
    
    if (xhr.status === 200) {
      const config = JSON.parse(xhr.responseText);
      console.log('⚡ [GlobalSiteConfig] Loaded from runtime file:', {
        siteId: config.siteId,
        projectId: config.projectId,
        projectType: config.projectType
      });
      return config;
    }
  } catch (error) {
    console.warn('⚠️ [GlobalSiteConfig] Failed to load site config:', error);
  }
  
  return null;
}

/**
 * Initialize global configuration (called once during bootstrap)
 */
function initializeGlobalConfig(): GlobalConfig {
  if (globalConfig.loadAttempted) {
    return globalConfig;
  }
  
  console.log('🚀 [GlobalSiteConfig] Initializing global site configuration...');
  globalConfig.loadAttempted = true;
  
  // Handle development environment overrides first
  const isDevelopment = import.meta.env.MODE === 'development';
  const hasDevSiteOverride = typeof import.meta.env.VITE_DEV_SITE_OVERRIDE === 'string' &&
    import.meta.env.VITE_DEV_SITE_OVERRIDE.length > 0;
  if (isDevelopment && hasDevSiteOverride && import.meta.env.VITE_PRIVATE_LABEL_OVERRIDE !== undefined) {
    const overrideType = import.meta.env.VITE_PRIVATE_LABEL_OVERRIDE === 'true' ? 'private_label' : 'white_label';
    console.log('🔧 [GlobalSiteConfig] Using development override:', overrideType);
    
    globalConfig.projectType = overrideType;
    globalConfig.projectId = null; // Will be resolved from other sources
    globalConfig.isLoaded = true;
    
    // Still try to load site config for other metadata
    const siteConfig = loadSiteConfigSync();
    if (siteConfig) {
      globalConfig.siteConfig = siteConfig;
      // If overriding to private_label, allow projectId; if white_label, do not set a projectId
      if (overrideType === 'private_label') {
        // Use only the runtime site-config.json for projectId (no env/localStorage)
        if (siteConfig.projectId) {
          globalConfig.projectId = siteConfig.projectId;
          console.log('🔑 [GlobalSiteConfig] Dev private_label projectId from site-config.json:', siteConfig.projectId);
        }
      } else {
        // Ensure no projectId leaks into white label during development override
        globalConfig.projectId = null;
      }
    }
    
    return globalConfig;
  }
  
  // Load site configuration
  const siteConfig = loadSiteConfigSync();

  if (isDevelopment && !hasDevSiteOverride) {
    const shouldUseFallback =
      !siteConfig ||
      siteConfig.projectId !== null ||
      siteConfig.projectType !== 'white_label';

    if (shouldUseFallback) {
      console.log('🧪 [GlobalSiteConfig] Applying local development fallback configuration (no site override active).', {
        originalSiteId: siteConfig?.siteId,
        originalProjectId: siteConfig?.projectId,
        originalProjectType: siteConfig?.projectType
      });

      globalConfig.siteConfig = { ...LOCAL_DEV_FALLBACK };
      globalConfig.projectType = LOCAL_DEV_FALLBACK.projectType;
      globalConfig.projectId = LOCAL_DEV_FALLBACK.projectId;
      globalConfig.isLoaded = true;
      return globalConfig;
    }
  }
  
  if (siteConfig) {
    globalConfig.siteConfig = siteConfig;
    globalConfig.projectType = siteConfig.projectType;
    globalConfig.projectId = siteConfig.projectId;
    globalConfig.isLoaded = true;
    
    console.log('✅ [GlobalSiteConfig] Configuration loaded successfully:', {
      siteId: siteConfig.siteId,
      projectType: siteConfig.projectType,
      projectId: siteConfig.projectId
    });
  } else {
    console.warn('⚠️ [GlobalSiteConfig] No site config found, using defaults');
    
    // Fallback logic
    const domain = typeof window !== 'undefined' ? window.location.origin : '';
    const isLocalDev = domain.includes('localhost') || domain.includes('127.0.0.1');
    
    globalConfig.projectType = isLocalDev ? 'private_label' : 'white_label';
    globalConfig.projectId = null;
    globalConfig.isLoaded = true;
    
    console.log('🔧 [GlobalSiteConfig] Using fallback configuration:', {
      projectType: globalConfig.projectType,
      source: isLocalDev ? 'localhost_fallback' : 'production_fallback'
    });
  }
  
  return globalConfig;
}

/**
 * Get project type - guaranteed to return a value
 */
export function getProjectType(): 'white_label' | 'private_label' {
  if (!globalConfig.loadAttempted) {
    initializeGlobalConfig();
  }
  
  return globalConfig.projectType || 'white_label';
}

/**
 * Get project ID - may return null for white label or unresolved contexts
 */
export function getProjectId(): string | null {
  if (!globalConfig.loadAttempted) {
    initializeGlobalConfig();
  }
  
  return globalConfig.projectId;
}

/**
 * Get full site configuration
 */
export function getSiteConfig(): SiteConfig | null {
  if (!globalConfig.loadAttempted) {
    initializeGlobalConfig();
  }
  
  return globalConfig.siteConfig;
}

/**
 * Check if this is a private label app
 */
export function isPrivateLabel(): boolean {
  return getProjectType() === 'private_label';
}

/**
 * Check if this is a white label app  
 */
export function isWhiteLabel(): boolean {
  return getProjectType() === 'white_label';
}

/**
 * Get configuration state information
 */
export function getConfigState(): {
  isLoaded: boolean;
  loadAttempted: boolean;
  hasProjectId: boolean;
  hasSiteConfig: boolean;
} {
  return {
    isLoaded: globalConfig.isLoaded,
    loadAttempted: globalConfig.loadAttempted,
    hasProjectId: !!globalConfig.projectId,
    hasSiteConfig: !!globalConfig.siteConfig
  };
}

/**
 * Force reload configuration (for debugging)
 */
export function reloadConfig(): GlobalConfig {
  globalConfig = {
    siteConfig: null,
    projectType: null,
    projectId: null,
    isLoaded: false,
    loadAttempted: false
  };
  
  return initializeGlobalConfig();
}

/**
 * Update project ID dynamically (for white label route resolution)
 */
export function setProjectId(projectId: string | null): void {
  globalConfig.projectId = projectId;
  console.log('🔄 [GlobalSiteConfig] Project ID updated:', projectId);
}

// Initialize immediately when this module is imported
initializeGlobalConfig();

// Make available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).globalSiteConfig = {
    getProjectType,
    getProjectId,
    getSiteConfig,
    isPrivateLabel,
    isWhiteLabel,
    getConfigState,
    reloadConfig,
    setProjectId
  };
}

// Export the raw config for advanced use cases
export { globalConfig };
